import { Component, ViewChild, AfterViewInit, ElementRef, Output, EventEmitter, OnDestroy, Input } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import * as d3 from 'd3';

declare var L;


/*
 * data-layer-highlight defines a class to highlight other geojson point data.
 *   https://leafletjs.com/examples/quick-start/ to see circle example
 * https://medium.com/simars/rxjs-create-your-own-observables-directly-from-event-sources-2b7c96170e17
 */

@Component({
	selector: 'data-layer-highlight',
	standalone: false,
	template: ` 
			  <div id="searchBox" [ngClass]="{doNotDisplay:!searchBoxVisible}" style="display:block" 
			  	title="Search map for a station name or identifier in the currently selected 'Data Display Layer.'  The map will automtically zoom to matched stations and the number of matches displayed next to each data layer.">
				<span class="ui-inputgroup-addon" style="margin-left:-15px">
					<i class="pi pi-search" style="color:white"></i>
				<input #searchInput type="text" pInputText placeholder="Search"
					style="width:100px; height:25px; margin-left: 10px; background-color:#f3f3f3"/>
				<label *ngIf="displayMatchesTotal==true" style="color:white; margin:0px -10px">({{numHighlighted}} matches)</label>
				</span>
			</div>
	`,
	styleUrls: ['app.component.css']
})
export class DataLayerHighlightComponent extends DataLayerBaseComponent implements AfterViewInit {
	// allow monitoring and access to searchInput input element
	@ViewChild('searchInput')
	searchInput: ElementRef;

	@Input() displayMatchesTotal: boolean = true;

	// when a search event occurs, emit value of what user has typed into searchInput
	@Output() searchItemEvent = new EventEmitter<string>();

	searchBoxVisible = true;
	searchString;
	searchSubscription = <any>Subscription;
	numHighlighted = 0;
	zoomRequired = false;  // helps in keeping track about whether drawMarkers was called as a result of a search or coming from another view (dashboard, etc)

	constructor(
		mapService: MapService, dataLayerService: DataLayerService, configService: ConfigService, userMessageService: UserMessageService ) 
		{
			super(mapService, dataLayerService, configService, userMessageService);
			this.active = true;
		}

	// Set up the search input subscription.  Every time a user types in the search input and there is more than a .8 second break, the search routine is called.
	// According to https://blog.angular-university.io/angular-viewchild/
	// elements referred to in ViewChild are not reliably available  until ngAfterViewInit so shouldn't be referenced in ngOnInit
	ngAfterViewInit() {
		const search$ = fromEvent<any>(this.searchInput.nativeElement, 'keyup' )
			.pipe (
				map(event => event.target.value),
				debounceTime(800)
			);
		this.searchSubscription = search$
			.subscribe ( s => {
				this.zoomRequired = true;
				this.searchString = s;
				// emit to parent component that a search string has been entered
				this.searchItemEvent.emit(s);
				// search the current layers for matches
				this.parseDataFile();
			});

		// subscribe to changes that should retrigger a filter event like changing data types displayed on the map or changing to the map view from dashboard, for example.
		this.dataLayerService.filterEvent$
			.subscribe ( reason => {
				this.search();
			});
	}

	// Even though this component will only be created once upon initialization of the application and there really is no need to unsubscribe,
	// unsubscribe is being called out of good Angular practices.
	ngOnDestroy() {
		this.searchSubscription.unsubscribe();
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// revise this function - we aren't reading in new data, just processing 
	// first check whether the information has been read.
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established
			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		this.active = true;
	}

	drawMarkers () {
		// we don't have a single geojson file so need to create a layer, add circles to that layer, add layer
		this.numHighlighted = this.value.length;
		console.log('Drawing ' + this.numHighlighted + ' highlighted stations.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}

		this.mapLayer = L.featureGroup();  // featureGroup rather than layerGroup; layerGroup didn't allow getBounds() below
		this.value.forEach(feature => {
			const circle = L.circleMarker([feature.properties.stationSummary.latitude, feature.properties.stationSummary.longitude], {
				color: 'red',
				fillColor: 'yellow',
				// fillColor: '#f03',
				fillOpacity: 0.5,
				radius: 10
			});
			this.mapLayer.addLayer(circle)
		});

		// if zoomRequired is true, it's because the user just entered a string into the search box and the app should zoom to those stations
		// However, subsequent calls to drawMarkers should not zoom.
		if ( this.zoomRequired == true ) {
			this.zoomToMarkers();
			this.zoomRequired = false;
		}

	}

	zoomToMarkers() {
		// if there are no stations to highlight, reset the map to the default zoom level
		// otherwise, zoom map into the bounds of the stations
		if ( this.numHighlighted === 0 ) {
			this.mapService.setZoomAroundDefault();
			console.log('Zooming around default');
		} else {
			this.mapService.map.addLayer(this.mapLayer);
			this.mapService.map.fitBounds(this.mapLayer.getBounds().pad(.20));

			// For some reason, when the user clicks on a view other than the map (dashboard, summary, etc.),
			// the bounds assigned here are correct but the map draws zoomed very far out.  It presents like
			// a bug in Leaflet because if the fitBounds line is commented out, that doesn't happen.
			// Fond a bug https://github.com/Leaflet/Leaflet/issues/3249 that claims similar behavior.
			// As a fix, I'm waiting 200 milliseconds and then calling it again.  Fortunately,
			// when the map is already zoomed in, this redraw is impercievable and when it's zoomed out,
			// the redraw happens very quickly.
			const self = this;
			setTimeout(function() {
				// I'd like to animate but the options seem to be ignored.
				self.mapService.map.fitBounds(self.mapLayer.getBounds().pad(.20), {animate: true, duration: 2});
			}, 200);
		}
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}
	}

	// To relieve possible confusion when calling from outside this class, I'm including the search function which only calls parseDataFile.
	// It does not reset this.searchString.
	search() {
		this.parseDataFile();
	}

	// This routine name is a bit of a misnomer but is being used to maintain consistency with other data-layer components.
	// Each active data layer will be searched for a numId or name which contains the searchString.
	// The comparison is case-insensitive.
	parseDataFile () {
		if ( this.searchString === '' || this.searchString === null || this.searchString === undefined ) {
			this.value = [];
			this.valueObservable.next(this.value);
			return;
		}
		console.log ( 'Searching for ' + this.searchString );
		const searchStringAllCaps = this.searchString.toUpperCase();
		let allStations = [];
		this.dataLayerService.getDataLayers().then(dataLayers => {
			// console.log(this);
			// console.log(dataLayers);
			dataLayers.forEach(function(item) {
				if (item.active === true) {
					// console.log(item.value);
					// search value for id or name contains searchBoxString
					// if mataches, add it to the array of features to highlight
					const matchingFeatures = item.value.features.filter ( feature => {
						return ('' + feature.properties.stationSummary.numId).includes(this.searchString) ||
								feature.properties.stationSummary.name.toUpperCase().includes(searchStringAllCaps);
					})
					// console.log(matchingFeatures);
					allStations = allStations.concat(matchingFeatures);
				}
			}, this);

			this.value = allStations;
			// console.log(this.value);
			this.valueObservable.next(allStations);
		});
	}

}
